import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const COLORS = ['#22C55E', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const MetricCard = ({ title, value, change, icon, iconBg, description, loading }) => (
  <div className="bg-card border border-border rounded-xl p-5">
    {loading ? (
      <div className="space-y-3 animate-pulse">
        <div className="h-4 bg-muted rounded w-1/2" />
        <div className="h-8 bg-muted rounded w-3/4" />
        <div className="h-3 bg-muted rounded w-full" />
      </div>
    ) : (
      <>
        <div className="flex items-center justify-between mb-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconBg}`}>
            <Icon name={icon} size={18} className="text-white" />
          </div>
          {change !== undefined && change !== '' && (
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${
              String(change)?.startsWith('+') ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
            }`}>{change}</span>
          )}
        </div>
        <p className="text-2xl font-bold text-card-foreground">{value}</p>
        <p className="text-sm font-medium text-card-foreground mt-1">{title}</p>
        {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
      </>
    )}
  </div>
);

// Helper: compute date range boundaries from a dateRange string
const getDateBounds = (dateRange) => {
  const now = new Date();
  let startDate = null;
  if (dateRange === 'last-7-days') {
    startDate = new Date(now);
    startDate?.setDate(now?.getDate() - 7);
  } else if (dateRange === 'last-30-days') {
    startDate = new Date(now);
    startDate?.setDate(now?.getDate() - 30);
  } else if (dateRange === 'last-90-days') {
    startDate = new Date(now);
    startDate?.setDate(now?.getDate() - 90);
  } else if (dateRange === 'this-year') {
    startDate = new Date(now.getFullYear(), 0, 1);
  }
  return {
    startISO: startDate ? startDate?.toISOString() : null,
    endISO: now?.toISOString(),
  };
};

const Reports = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('last-30-days');
  const [metrics, setMetrics] = useState({});
  const [revenueData, setRevenueData] = useState([]);
  const [topServices, setTopServices] = useState([]);
  const [techPerformance, setTechPerformance] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const { profile } = useAuth();

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'revenue', label: 'Revenue & Expenses', icon: 'DollarSign' },
    { id: 'marketing', label: 'Marketing', icon: 'TrendingUp' },
    { id: 'operations', label: 'Operations', icon: 'Briefcase' },
  ];

  const fetchAllMetrics = useCallback(async () => {
    if (!profile?.org_id) return;
    setLoading(true);
    try {
      const { startISO } = getDateBounds(dateRange);

      // Build base queries with date filter applied
      let jobsQuery = supabase?.from('jobs')?.select('*')?.eq('org_id', profile?.org_id)?.is('deleted_at', null);
      let leadsQuery = supabase?.from('leads')?.select('*')?.eq('org_id', profile?.org_id)?.is('deleted_at', null);
      let dealsQuery = supabase?.from('deals')?.select('*')?.eq('org_id', profile?.org_id)?.is('deleted_at', null);

      if (startISO) {
        jobsQuery = jobsQuery?.gte('created_at', startISO);
        leadsQuery = leadsQuery?.gte('created_at', startISO);
        dealsQuery = dealsQuery?.gte('created_at', startISO);
      }

      const [
        jobsResult,
        leadsResult,
        dealsResult,
        contactsResult,
        allJobsResult,
      ] = await Promise.all([
        jobsQuery,
        leadsQuery,
        dealsQuery,
        supabase?.from('contacts')?.select('id', { count: 'exact', head: true })?.eq('org_id', profile?.org_id)?.is('deleted_at', null),
        supabase?.from('jobs')?.select('total_cost, value, status, created_at')?.eq('org_id', profile?.org_id)?.is('deleted_at', null),
      ]);

      const { data: jobs, error: jobsError } = jobsResult;
      const { data: leads, error: leadsError } = leadsResult;
      const { data: deals, error: dealsError } = dealsResult;
      const { count: contacts } = contactsResult;
      const { data: allJobs } = allJobsResult;

      if (jobsError) console.error('jobs error:', jobsError);
      if (leadsError) console.error('leads error:', leadsError);
      if (dealsError) console.error('deals error:', dealsError);

      const jobList = jobs || [];
      const leadList = leads || [];
      const dealList = deals || [];
      const allJobsList = allJobs || [];

      // Revenue metrics — use jobs total_cost or value (invoices may be empty)
      const completedJobsList = jobList?.filter(j => j?.status === 'completed');
      const totalRevenue = completedJobsList?.reduce((s, j) => s + (Number(j?.total_cost) || Number(j?.value) || 0), 0);
      const pendingRevenue = jobList?.filter(j => j?.status !== 'completed' && j?.status !== 'cancelled')?.reduce((s, j) => s + (Number(j?.total_cost) || Number(j?.value) || 0), 0);
      const completedJobs = completedJobsList?.length;
      const avgJobValue = completedJobs > 0 ? totalRevenue / completedJobs : 0;

      // Lead & conversion metrics
      const totalLeads = leadList?.length;
      const convertedLeads = leadList?.filter(l => l?.status === 'converted')?.length;
      const conversionRate = totalLeads > 0 ? ((convertedLeads / totalLeads) * 100)?.toFixed(1) : '0.0';

      // Deal metrics
      const totalDealValue = dealList?.reduce((s, d) => s + (Number(d?.value) || 0), 0);
      const avgDealSize = dealList?.length > 0 ? totalDealValue / dealList?.length : 0;

      setMetrics({
        totalRevenue,
        pendingRevenue,
        completedJobs,
        avgJobValue,
        totalLeads,
        conversionRate,
        totalDeals: dealList?.length,
        avgDealSize,
        totalContacts: contacts || 0,
        jobCount: jobList?.length,
      });

      // Revenue by month (last 6 months) — always use all jobs regardless of date filter
      const now = new Date();
      const monthlyData = [];
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthName = d?.toLocaleString('default', { month: 'short' });
        const monthJobs = allJobsList?.filter(j => {
          const jDate = new Date(j.created_at);
          return jDate?.getMonth() === d?.getMonth() && jDate?.getFullYear() === d?.getFullYear();
        });
        const revenue = monthJobs?.filter(j => j?.status === 'completed')?.reduce((s, j) => s + (Number(j?.total_cost) || Number(j?.value) || 0), 0);
        const expenses = revenue * 0.35;
        monthlyData?.push({
          month: monthName,
          revenue: Math.round(revenue),
          expenses: Math.round(expenses),
          profit: Math.round(revenue - expenses),
        });
      }
      setRevenueData(monthlyData);

      // Top services from jobs
      const serviceMap = {};
      jobList?.forEach(j => {
        const svc = j?.service_type || j?.job_type || 'General';
        serviceMap[svc] = (serviceMap?.[svc] || 0) + 1;
      });
      const topSvc = Object.entries(serviceMap)?.sort((a, b) => b?.[1] - a?.[1])?.slice(0, 5)?.map(([name, count]) => ({ name, count }));
      setTopServices(topSvc);

      // Lead sources
      const sourceMap = {};
      leadList?.forEach(l => {
        const src = l?.source || 'Direct';
        sourceMap[src] = (sourceMap?.[src] || 0) + 1;
      });
      const srcData = Object.entries(sourceMap)?.map(([name, value]) => ({ name, value }));
      setLeadSources(srcData);

      // Technician performance (from jobs assigned_to)
      const techMap = {};
      jobList?.forEach(j => {
        if (j?.assigned_to) {
          if (!techMap?.[j?.assigned_to]) techMap[j.assigned_to] = { jobs: 0, completed: 0 };
          techMap[j.assigned_to].jobs++;
          if (j?.status === 'completed') techMap[j.assigned_to].completed++;
        }
      });
      const techData = Object.entries(techMap)?.slice(0, 5)?.map(([name, data]) => ({
        name: name?.length > 12 ? name?.substring(0, 12) + '...' : name,
        jobs: data?.jobs,
        completed: data?.completed,
        rate: data?.jobs > 0 ? Math.round((data?.completed / data?.jobs) * 100) : 0,
      }));
      setTechPerformance(techData);

    } catch (err) {
      console.error('Reports error:', err);
    } finally {
      setLoading(false);
    }
  }, [profile?.org_id, dateRange]);

  useEffect(() => { fetchAllMetrics(); }, [fetchAllMetrics]);

  const fmt = (n) => {
    if (!n || isNaN(n)) return '$0';
    return n >= 1000 ? `$${(n / 1000)?.toFixed(1)}k` : `$${Math.round(n)?.toLocaleString()}`;
  };

  const dateRangeLabel = {
    'last-7-days': 'Last 7 days',
    'last-30-days': 'Last 30 days',
    'last-90-days': 'Last 90 days',
    'this-year': 'This year',
  };

  return (
    <>
      <Helmet>
        <title>Reports - Wilcart CRM</title>
        <meta name="description" content="Comprehensive business analytics and performance metrics." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)} isSidebarOpen={isSidebarOpen} />
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <main className="lg:ml-64 pt-24 lg:pt-28">
          <div className="p-4 lg:p-8">
            {/* Header */}
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                  <h1 className="text-3xl font-bold text-foreground">Reports & Analytics</h1>
                  <p className="text-muted-foreground mt-1">
                    Business metrics — {dateRangeLabel?.[dateRange] || 'All time'}
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <select
                    value={dateRange}
                    onChange={e => setDateRange(e?.target?.value)}
                    className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="last-7-days">Last 7 days</option>
                    <option value="last-30-days">Last 30 days</option>
                    <option value="last-90-days">Last 90 days</option>
                    <option value="this-year">This year</option>
                  </select>
                  <Button variant="outline" iconName="Download" iconPosition="left" onClick={() => window.print()}>Export</Button>
                </div>
              </div>
            </motion.div>

            {/* Tabs */}
            <div className="flex space-x-1 bg-muted p-1 rounded-lg mb-6 overflow-x-auto">
              {tabs?.map(tab => (
                <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab?.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}>
                  <Icon name={tab?.icon} size={15} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <MetricCard title="Total Revenue" value={fmt(metrics?.totalRevenue || 0)} change="" icon="DollarSign" iconBg="bg-green-500" description="From completed jobs" loading={loading} />
                  <MetricCard title="Pending Revenue" value={fmt(metrics?.pendingRevenue || 0)} change="" icon="Clock" iconBg="bg-yellow-500" description="Active/scheduled jobs" loading={loading} />
                  <MetricCard title="Jobs Completed" value={String(metrics?.completedJobs || 0)} change="" icon="CheckCircle" iconBg="bg-blue-500" description="In selected period" loading={loading} />
                  <MetricCard title="Avg Job Value" value={fmt(metrics?.avgJobValue || 0)} change="" icon="TrendingUp" iconBg="bg-purple-500" description="Per completed job" loading={loading} />
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <MetricCard title="Total Leads" value={String(metrics?.totalLeads || 0)} change="" icon="UserPlus" iconBg="bg-pink-500" description="In selected period" loading={loading} />
                  <MetricCard title="Conversion Rate" value={`${metrics?.conversionRate || 0}%`} change="" icon="Target" iconBg="bg-indigo-500" description="Lead to customer" loading={loading} />
                  <MetricCard title="Total Deals" value={String(metrics?.totalDeals || 0)} change="" icon="Briefcase" iconBg="bg-orange-500" description="In pipeline" loading={loading} />
                  <MetricCard title="Avg Deal Size" value={fmt(metrics?.avgDealSize || 0)} change="" icon="BarChart3" iconBg="bg-teal-500" description="Mean deal value" loading={loading} />
                </div>

                {/* Revenue Chart */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <h3 className="font-semibold text-card-foreground mb-4">Revenue vs Expenses (Last 6 Months)</h3>
                  {loading ? <div className="h-64 bg-muted animate-pulse rounded-lg" /> : revenueData?.every(d => d?.revenue === 0) ? (
                    <div className="flex flex-col items-center justify-center h-64">
                      <Icon name="BarChart3" size={40} className="text-muted-foreground mb-3" />
                      <p className="text-muted-foreground text-sm">No completed jobs with revenue yet</p>
                      <p className="text-muted-foreground text-xs mt-1">Complete jobs and add costs to see revenue data</p>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={revenueData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                        <XAxis dataKey="month" tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }} />
                        <YAxis tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }} tickFormatter={v => `$${v >= 1000 ? (v / 1000)?.toFixed(0)+'k' : v}`} />
                        <Tooltip formatter={(v, n) => [`$${v?.toLocaleString()}`, n]} contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '8px', color: 'var(--color-card-foreground)' }} />
                        <Legend />
                        <Bar dataKey="revenue" fill="#22C55E" name="Revenue" radius={[4,4,0,0]} />
                        <Bar dataKey="expenses" fill="#EF4444" name="Expenses" radius={[4,4,0,0]} />
                        <Bar dataKey="profit" fill="#3B82F6" name="Profit" radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Top Services */}
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h3 className="font-semibold text-card-foreground mb-4">Top Services</h3>
                    {loading ? <div className="h-40 bg-muted animate-pulse rounded-lg" /> : topServices?.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">No job data in selected period</p>
                    ) : (
                      <div className="space-y-3">
                        {topServices?.map((svc, i) => (
                          <div key={svc?.name} className="flex items-center space-x-3">
                            <span className="text-xs font-bold text-muted-foreground w-4">{i + 1}</span>
                            <div className="flex-1">
                              <div className="flex justify-between mb-1">
                                <span className="text-sm font-medium text-card-foreground">{svc?.name}</span>
                                <span className="text-sm text-muted-foreground">{svc?.count} jobs</span>
                              </div>
                              <div className="h-2 bg-muted rounded-full overflow-hidden">
                                <div className="h-full bg-primary rounded-full" style={{ width: `${(svc?.count / (topServices?.[0]?.count || 1)) * 100}%` }} />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Lead Sources */}
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h3 className="font-semibold text-card-foreground mb-4">Lead Sources</h3>
                    {loading ? <div className="h-40 bg-muted animate-pulse rounded-lg" /> : leadSources?.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">No lead data in selected period</p>
                    ) : (
                      <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                          <Pie data={leadSources} cx="50%" cy="50%" outerRadius={70} dataKey="value" nameKey="name" label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`} labelLine={false}>
                            {leadSources?.map((_, i) => <Cell key={i} fill={COLORS?.[i % COLORS?.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '8px', color: 'var(--color-card-foreground)' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Revenue & Expenses Tab */}
            {activeTab === 'revenue' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <MetricCard title="Total Revenue" value={fmt(metrics?.totalRevenue || 0)} change="" icon="DollarSign" iconBg="bg-green-500" description="Completed jobs" loading={loading} />
                  <MetricCard title="Estimated Expenses" value={fmt((metrics?.totalRevenue || 0) * 0.35)} change="" icon="TrendingDown" iconBg="bg-red-500" description="~35% of revenue" loading={loading} />
                  <MetricCard title="Net Profit" value={fmt((metrics?.totalRevenue || 0) * 0.65)} change="" icon="PiggyBank" iconBg="bg-blue-500" description="Estimated profit" loading={loading} />
                </div>
                <div className="bg-card border border-border rounded-xl p-6">
                  <h3 className="font-semibold text-card-foreground mb-4">Monthly Revenue Trend</h3>
                  {loading ? <div className="h-64 bg-muted animate-pulse rounded-lg" /> : (
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={revenueData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                        <XAxis dataKey="month" tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }} />
                        <YAxis tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }} tickFormatter={v => `$${v >= 1000 ? (v / 1000)?.toFixed(0)+'k' : v}`} />
                        <Tooltip formatter={(v) => [`$${v?.toLocaleString()}`]} contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '8px', color: 'var(--color-card-foreground)' }} />
                        <Legend />
                        <Line type="monotone" dataKey="revenue" stroke="#22C55E" strokeWidth={2} dot={{ fill: '#22C55E' }} name="Revenue" />
                        <Line type="monotone" dataKey="expenses" stroke="#EF4444" strokeWidth={2} dot={{ fill: '#EF4444' }} name="Expenses" />
                        <Line type="monotone" dataKey="profit" stroke="#3B82F6" strokeWidth={2} dot={{ fill: '#3B82F6' }} name="Profit" />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <MetricCard title="Pending Revenue" value={fmt(metrics?.pendingRevenue || 0)} change="" icon="Clock" iconBg="bg-yellow-500" description="Active/scheduled jobs" loading={loading} />
                  <MetricCard title="Avg Job Value" value={fmt(metrics?.avgJobValue || 0)} change="" icon="BarChart2" iconBg="bg-teal-500" description="Per completed job" loading={loading} />
                  <MetricCard title="Jobs Count" value={String(metrics?.jobCount || 0)} change="" icon="Briefcase" iconBg="bg-orange-500" description="Total jobs" loading={loading} />
                  <MetricCard title="Completed Jobs" value={String(metrics?.completedJobs || 0)} change="" icon="CheckCircle" iconBg="bg-green-500" description="Finished" loading={loading} />
                </div>
              </motion.div>
            )}

            {/* Marketing Tab */}
            {activeTab === 'marketing' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <MetricCard title="Total Leads" value={String(metrics?.totalLeads || 0)} change="" icon="UserPlus" iconBg="bg-pink-500" description="In selected period" loading={loading} />
                  <MetricCard title="Conversion Rate" value={`${metrics?.conversionRate || 0}%`} change="" icon="Target" iconBg="bg-indigo-500" description="Lead to customer" loading={loading} />
                  <MetricCard title="Total Contacts" value={String(metrics?.totalContacts || 0)} change="" icon="Users" iconBg="bg-blue-500" description="In CRM" loading={loading} />
                  <MetricCard title="Avg Deal Size" value={fmt(metrics?.avgDealSize || 0)} change="" icon="DollarSign" iconBg="bg-green-500" description="Mean value" loading={loading} />
                </div>
                <div className="bg-card border border-border rounded-xl p-6">
                  <h3 className="font-semibold text-card-foreground mb-4">Lead Sources Breakdown</h3>
                  {loading ? <div className="h-64 bg-muted animate-pulse rounded-lg" /> : leadSources?.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16">
                      <Icon name="TrendingUp" size={40} className="text-muted-foreground mb-3" />
                      <p className="text-muted-foreground">No lead data in selected period</p>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={leadSources} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                        <XAxis type="number" tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }} />
                        <YAxis dataKey="name" type="category" tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }} width={80} />
                        <Tooltip contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '8px', color: 'var(--color-card-foreground)' }} />
                        <Bar dataKey="value" fill="#22C55E" name="Leads" radius={[0,4,4,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </motion.div>
            )}

            {/* Operations Tab */}
            {activeTab === 'operations' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <MetricCard title="Total Jobs" value={String(metrics?.jobCount || 0)} change="" icon="Briefcase" iconBg="bg-blue-500" description="In selected period" loading={loading} />
                  <MetricCard title="Completed Jobs" value={String(metrics?.completedJobs || 0)} change="" icon="CheckCircle" iconBg="bg-green-500" description="Finished" loading={loading} />
                  <MetricCard title="Completion Rate" value={metrics?.jobCount > 0 ? `${Math.round((metrics?.completedJobs / metrics?.jobCount) * 100)}%` : '0%'} change="" icon="TrendingUp" iconBg="bg-purple-500" description="Jobs completed" loading={loading} />
                  <MetricCard title="Avg Job Value" value={fmt(metrics?.avgJobValue || 0)} change="" icon="DollarSign" iconBg="bg-orange-500" description="Revenue per job" loading={loading} />
                </div>

                {/* Technician Performance */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <h3 className="font-semibold text-card-foreground mb-4">Technician Performance</h3>
                  {loading ? <div className="h-40 bg-muted animate-pulse rounded-lg" /> : techPerformance?.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <Icon name="Users" size={36} className="text-muted-foreground mb-3" />
                      <p className="text-muted-foreground text-sm">No technician data in selected period</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-2 px-3 text-sm font-medium text-muted-foreground">Technician</th>
                            <th className="text-left py-2 px-3 text-sm font-medium text-muted-foreground">Total Jobs</th>
                            <th className="text-left py-2 px-3 text-sm font-medium text-muted-foreground">Completed</th>
                            <th className="text-left py-2 px-3 text-sm font-medium text-muted-foreground">Completion Rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {techPerformance?.map(tech => (
                            <tr key={tech?.name} className="border-b border-border hover:bg-muted/30">
                              <td className="py-3 px-3 font-medium text-card-foreground text-sm">{tech?.name}</td>
                              <td className="py-3 px-3 text-sm text-muted-foreground">{tech?.jobs}</td>
                              <td className="py-3 px-3 text-sm text-muted-foreground">{tech?.completed}</td>
                              <td className="py-3 px-3">
                                <div className="flex items-center space-x-2">
                                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                                    <div className="h-full bg-primary rounded-full" style={{ width: `${tech?.rate}%` }} />
                                  </div>
                                  <span className="text-sm font-medium text-card-foreground">{tech?.rate}%</span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Top Services */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <h3 className="font-semibold text-card-foreground mb-4">Top Services by Job Count</h3>
                  {loading ? <div className="h-40 bg-muted animate-pulse rounded-lg" /> : topServices?.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">No service data in selected period</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={topServices}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                        <XAxis dataKey="name" tick={{ fill: 'var(--color-muted-foreground)', fontSize: 11 }} />
                        <YAxis tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }} />
                        <Tooltip contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '8px', color: 'var(--color-card-foreground)' }} />
                        <Bar dataKey="count" fill="#22C55E" name="Jobs" radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default Reports;